import { Chip } from '@lifeomic/chroma-react/components/Chip';
import { FormattedMessage } from 'react-intl';
import { GetClasses } from '@lifeomic/chroma-react/typeUtils';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import {
  Popover,
  PopoverList,
  PopoverItem,
} from '@lifeomic/chroma-react/components/Popover';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { SEA_GREEN } from '../../theme/phc/styles/content';
import Fade from '@mui/material/Fade';
import React from 'react';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    overflowX: 'scroll',
    padding: theme.spacing(1),
  },
  chip: {
    marginTop: 0,
    marginBottom: 0,
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  hasChips: {
    position: 'relative',
    '&::after': {
      content: '""',
      display: 'inline-block',
      backgroundColor: SEA_GREEN,
      width: theme.pxToRem(10),
      height: theme.pxToRem(10),
      borderRadius: '50%',
      position: 'absolute',
      top: theme.pxToRem(10),
      right: theme.pxToRem(10),
    },
  },
  noFiltersText: {
    color: theme.palette.text.secondary,
  },
  popover: {
    maxWidth: theme.pxToRem(700),
    maxHeight: theme.pxToRem(500),
    overflowY: 'scroll',
  },
  popoverList: {
    padding: 0,
    margin: 0,
  },
  popoverItem: {
    padding: theme.pxToRem(8),
  },
}));

export type BagOfChipsClasses = GetClasses<typeof useStyles>;

export interface BagOfChipsProps {
  chipLabel: ((option: any) => string) | string;
  onChipDelete?: (option: any) => void;
  options?: Array<any>;
  tooltipTitle?: ((option: any) => string) | string;
  className?: string;
  smallDisplay?: {
    icon: React.MemoExoticComponent<
      (props: React.SVGProps<SVGSVGElement>) => JSX.Element
    >;
    label: string;
  };
}

export const BagOfChips: React.FC<BagOfChipsProps> = ({
  chipLabel,
  onChipDelete,
  options = [],
  tooltipTitle,
  className: classNameProp,
  smallDisplay = undefined,
}) => {
  const classes = useStyles({});

  const renderChip = (option: any, key?: string) => (
    <PopoverItem className={classes.popoverItem}>
      <Chip
        key={key}
        className={classes.chip}
        label={
          typeof chipLabel === 'function'
            ? chipLabel(option)
            : option[chipLabel]
        }
        onDelete={() => onChipDelete && onChipDelete(option)}
      />
    </PopoverItem>
  );

  const renderContent = () => {
    if (options.length === 0) {
      return (
        <Fade in={true} timeout={1500}>
          <Text className={classes.noFiltersText} size="caption">
            <FormattedMessage
              id="filterContent.bagOfChips.noFiltersApplied"
              defaultMessage="There are no filters currently applied."
            />
          </Text>
        </Fade>
      );
    } else if (options) {
      return options.map((option, i) =>
        tooltipTitle ? (
          <Tooltip
            key={option.id || i}
            title={
              typeof tooltipTitle === 'function'
                ? tooltipTitle(option)
                : tooltipTitle
            }
            placement="top"
          >
            {renderChip(option)}
          </Tooltip>
        ) : (
          renderChip(option, option.id || i)
        ),
      );
    }
  };
  return smallDisplay ? (
    <Popover
      aria-label={smallDisplay.label}
      className={classes.popover}
      anchorElement={
        <div className={options.length > 0 ? classes.hasChips : ''}>
          <IconButton
            aria-label={smallDisplay.label}
            icon={smallDisplay.icon}
          />
        </div>
      }
      usePortal
      placement="bottom-end"
    >
      <Box padding={2}>
        <PopoverList className={classes.popoverList}>
          {renderContent()}
        </PopoverList>
      </Box>
    </Popover>
  ) : (
    <div className={clsx(classes.root, classNameProp)}>{renderContent()}</div>
  );
};
